export default {
  async getGroups() {
    // DEV ONLY, and only on a `/grant?fixture=` page — the acting-as picker
    // keeps groups whose `my_role` is admin, so without an answer here it is
    // offered nothing and never appears. Same guard and same dynamic import as
    // the preview short-circuit in grant.js; delete both with devFixtures.js.
    if (import.meta.env?.DEV && typeof window !== 'undefined'
        && new URLSearchParams(window.location.search).get('fixture')) {
      return (await import('../../pages/grant/devFixtures')).devGroups();
    }
    return this.request('/groups');
  },

  async createGroup(data) {
    return this.request('/groups', { method: 'POST', body: data });
  },

  async getGroup(groupId) {
    return this.request(`/groups/${groupId}`);
  },

  async updateGroup(groupId, data) {
    return this.request(`/groups/${groupId}`, { method: 'PUT', body: data });
  },

  // A file the group owns (its photo, say): the node belongs to the group.
  async uploadGroupFile(groupId, file, metadata = {}) {
    return this.uploadFileTo(`/groups/${groupId}/nodes`, file, metadata);
  },

  // The group's photo is a file it owns; null removes it. Admins only.
  async setGroupPhoto(groupId, fileId) {
    return this.updateGroup(groupId, { avatar_file_id: fileId });
  },

  async deleteGroup(groupId) {
    return this.request(`/groups/${groupId}`, { method: 'DELETE' });
  },

  // Members
  async getGroupMembers(groupId) {
    return this.request(`/groups/${groupId}/members`);
  },

  // `lastAdminChoice`: what the last admin leaving chose — 'transfer' (to the
  // longest-standing member) or 'close' (the same cleanup as Delete); 'ask', or
  // none, leaves like any admin unless you are the last, when the server answers
  // 409 LAST_ADMIN_CHOICE with the choices (`err.choices`, `err.heir`).
  async removeGroupMember(groupId, userId, { lastAdminChoice = null } = {}) {
    const q = lastAdminChoice ? new URLSearchParams({ last_admin_choice: lastAdminChoice }).toString() : '';
    return this.request(`/groups/${groupId}/members/${userId}${q ? `?${q}` : ''}`, { method: 'DELETE' });
  },

  async updateGroupMember(groupId, userId, data) {
    return this.request(`/groups/${groupId}/members/${userId}`, { method: 'PUT', body: data });
  },

  async promoteGroupMember(groupId, userId) {
    return this.request(`/groups/${groupId}/members/${userId}/promote`, { method: 'POST' });
  },

  // Invitations
  async inviteToGroup(groupId, data) {
    return this.request(`/groups/${groupId}/invitations`, { method: 'POST', body: data });
  },

  async getIncomingInvitations() {
    return this.request('/groups/invitations/incoming');
  },

  async getGroupInvitations(groupId) {
    return this.request(`/groups/${groupId}/invitations`);
  },

  async respondToInvitation(invitationId, action) {
    return this.request(`/groups/invitations/${invitationId}`, {
      method: 'PUT', body: { action }
    });
  },

  /** JOIN: accept an invitation, contributing the picked realms (none is allowed).
   *  This is the call that creates the membership — `respondToInvitation(…, 'accept')`
   *  only records the answer and points back at the grant page. */
  async authorizeGroupInvitation(invitationId, { privacyRealmIds = [] } = {}) {
    return this.request(`/groups/invitations/${invitationId}/authorize`, {
      method: 'POST', body: { privacy_realm_ids: privacyRealmIds },
    });
  },

  /** Already a member: set what you contribute to the group (none is allowed). */
  async authorizeGroupMembership(groupId, { privacyRealmIds = [] } = {}) {
    return this.request(`/groups/${groupId}/authorize`, {
      method: 'POST', body: { privacy_realm_ids: privacyRealmIds },
    });
  },

  async cancelInvitation(groupId, invitationId) {
    return this.request(`/groups/${groupId}/invitations/${invitationId}`, { method: 'DELETE' });
  },

  // Group Nodes
  async getGroupNodes(groupId, params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/groups/${groupId}/nodes${query ? '?' + query : ''}`);
  },

  async createGroupNode(groupId, data) {
    return this.request(`/groups/${groupId}/nodes`, { method: 'POST', body: data });
  },

  async updateGroupNode(groupId, nodeId, data) {
    return this.request(`/groups/${groupId}/nodes/${nodeId}`, { method: 'PUT', body: data });
  },

  async deleteGroupNode(groupId, nodeId) {
    return this.request(`/groups/${groupId}/nodes/${nodeId}`, { method: 'DELETE' });
  },

  async bulkDeleteGroupNodes(groupId, nodeIds) {
    return this.request(`/groups/${groupId}/nodes/bulk-delete`, {
      method: 'POST',
      body: { node_ids: nodeIds },
    });
  },

  // The group twins of restoreNode / purgeNode. The personal routes only reach
  // nodes you own, and a group node is owned by the group.
  async restoreGroupNode(groupId, nodeId, { force = false } = {}) {
    return this.request(`/groups/${groupId}/nodes/${nodeId}/restore${force ? '?force=true' : ''}`, {
      method: 'POST',
    });
  },

  async purgeGroupNode(groupId, nodeId) {
    return this.request(`/groups/${groupId}/nodes/${nodeId}/purge`, { method: 'DELETE' });
  },

  // Data Sharing
  async getGroupShare(groupId) {
    return this.request(`/groups/${groupId}/share`);
  },

  /** What the group's members share WITH it — each member's personal nodes their
   *  member → group EP allows. `memberId` narrows to one member. */
  async getGroupSharedNodes(groupId, { memberId, limit, offset } = {}) {
    const q = new URLSearchParams(Object.entries({ member_id: memberId, limit, offset })
      .filter(([, v]) => v != null && v !== '')).toString();
    return this.request(`/groups/${groupId}/shared${q ? `?${q}` : ''}`);
  },

  // "Share nothing": the contract with the group stays, emptied — never deleted.
  async revokeGroupShare(groupId) {
    return this.request(`/groups/${groupId}/share`, { method: 'DELETE' });
  },

  // Group Privacy Realms
  async getGroupPrivacyRealms(groupId) {
    const response = await this.request(`/groups/${groupId}/privacy-realms`);
    return response.privacy_realms || response.realms || response.profiles || [];
  },

  // Group Public Profile — the group's public-facing page (owned by the group's synthetic user)
  async getGroupPublicProfile(groupId) {
    return this.request(`/groups/${groupId}/public-profile`);
  },

  async setGroupPublicProfile(groupId, data) {
    return this.request(`/groups/${groupId}/public-profile`, { method: 'PUT', body: data });
  },

  async deleteGroupPublicProfile(groupId) {
    return this.request(`/groups/${groupId}/public-profile`, { method: 'DELETE' });
  },

  // Viewer side — the group's public nodes (what any logged-in user can see)
  async getGroupPublicView(groupId) {
    return this.request(`/groups/${groupId}/public`);
  },
};
